import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Checkbox,
  Row,
  Col,
  Card,
  Typography,
  Divider,
  Tag,
  Button,
  Space,
  Descriptions,
  theme
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Error, Success } from "../../Messages/Messages";
import { generalErrorAlert } from "../../GeneralErrorAlert";
import Tagcomp from "../../TagComp/Tagcomp";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import OfferBillingPanel from "./OfferBillingPanel";
import { countingFlags } from "../../../serviceProvider/components/gaeb/calc";
import "./Offers.css";

const { Title, Text } = Typography;
const { TextArea } = Input;

const CreateInvoiceModal = ({ open, onClose, offer, onSuccess }) => {
  const { t } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [, setLoadingInvoices] = useState(false);

  // Initialize form and item states when modal opens with offer data
  useEffect(() => {
    if (open && offer) {
      const initialQuantities = {};
      const initialSelected = [];

      // Unordered GAEB Bedarfs-/Alternativpositionen cannot be invoiced
      const counting = countingFlags(offer.items || []);
      (offer.items || []).forEach((item, index) => {
        const remaining = Math.max(0, (item.quantity || 0) - (item.invoicedQuantity || 0));
        if (remaining > 0 && counting[index]) {
          initialQuantities[item._id] = remaining;
          initialSelected.push(item._id);
        } else {
          initialQuantities[item._id] = 0;
        }
      });

      setQuantities(initialQuantities);
      setSelectedItemIds(initialSelected);

      form.setFieldsValue({
        title: `Rechnung - ${offer.title || offer.quotationNumber}`,
        dueDate: offer.dueDate ? dayjs(offer.dueDate) : dayjs().add(14, "day"),
        startDate: offer.startDate ? dayjs(offer.startDate) : dayjs(),
        endDate: offer.endDate ? dayjs(offer.endDate) : dayjs(),
        description: offer.description || ""
      });

      fetchLinkedInvoices(offer._id);
    } else {
      form.resetFields();
      setSelectedItemIds([]);
      setQuantities({});
      setExistingInvoices([]);
    }
  }, [open, offer, form]);

  const fetchLinkedInvoices = async offerId => {
    if (!offerId) return;
    setLoadingInvoices(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/quotations/${offerId}/invoices`,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        }
      );
      if (res.status === 200) {
        setExistingInvoices(res.data || []);
      }
    } catch (err) {
      console.error("Error loading linked invoices:", err);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleCheckboxChange = (itemId, checked) => {
    if (checked) {
      setSelectedItemIds(prev => [...prev, itemId]);
      const item = (offer.items || []).find(i => i._id === itemId);
      const remaining = item ? Math.max(0, (item.quantity || 0) - (item.invoicedQuantity || 0)) : 1;
      setQuantities(prev => ({
        ...prev,
        [itemId]: prev[itemId] > 0 ? prev[itemId] : remaining
      }));
    } else {
      setSelectedItemIds(prev => prev.filter(id => id !== itemId));
    }
  };

  const handleQuantityChange = (itemId, val) => {
    const item = (offer.items || []).find(i => i._id === itemId);
    const remaining = item ? Math.max(0, (item.quantity || 0) - (item.invoicedQuantity || 0)) : 0;
    const num = val === null ? 0 : Math.min(Math.max(0, val), remaining);

    setQuantities(prev => ({
      ...prev,
      [itemId]: num
    }));

    if (num > 0 && !selectedItemIds.includes(itemId)) {
      setSelectedItemIds(prev => [...prev, itemId]);
    } else if (num === 0 && selectedItemIds.includes(itemId)) {
      setSelectedItemIds(prev => prev.filter(id => id !== itemId));
    }
  };

  const availableItems = useMemo(() => {
    const counting = countingFlags(offer?.items || []);
    return (offer?.items || []).filter((item, index) => {
      const remaining = (item.quantity || 0) - (item.invoicedQuantity || 0);
      return remaining > 0 && counting[index];
    });
  }, [offer]);

  const handleSelectAll = e => {
    const checked = e.target.checked;
    if (checked) {
      const allAvailableIds = availableItems.map(i => i._id);
      setSelectedItemIds(allAvailableIds);
      const newQuantities = { ...quantities };
      availableItems.forEach(item => {
        const remaining = Math.max(0, (item.quantity || 0) - (item.invoicedQuantity || 0));
        newQuantities[item._id] = remaining;
      });
      setQuantities(newQuantities);
    } else {
      setSelectedItemIds([]);
    }
  };

  // Real-time calculation of subtotal, tax and grand total
  const financialSummary = useMemo(() => {
    let subTotal = 0;
    (offer?.items || []).forEach(item => {
      if (selectedItemIds.includes(item._id)) {
        const qty = quantities[item._id] || 0;
        const price = parseFloat(item.price || 0);
        subTotal += price * qty;
      }
    });

    const taxRate = parseFloat(offer?.tax || 0);
    const taxAmount = (subTotal * taxRate) / 100;
    const grandTotal = subTotal + taxAmount;

    return {
      subTotal,
      taxRate,
      taxAmount,
      grandTotal
    };
  }, [offer, selectedItemIds, quantities]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (selectedItemIds.length === 0) {
        Error(t("Please select at least one product"));
        return;
      }

      const selectedItemsPayload = [];
      for (const itemId of selectedItemIds) {
        const qty = quantities[itemId];
        const item = (offer.items || []).find(i => i._id === itemId);
        const remaining = item
          ? Math.max(0, (item.quantity || 0) - (item.invoicedQuantity || 0))
          : 0;

        if (!qty || qty <= 0) {
          Error(t("Quantity must be greater than zero"));
          return;
        }

        if (qty > remaining) {
          Error(
            `${t("Quantity cannot exceed the remaining quantity")} (${item?.description}: max ${remaining})`
          );
          return;
        }

        selectedItemsPayload.push({
          offerItemId: itemId,
          quantity: qty
        });
      }

      setLoading(true);

      const payload = {
        title: values.title,
        dueDate: values.dueDate ? values.dueDate.format("YYYY-MM-DD") : "",
        startDate: values.startDate ? values.startDate.format("YYYY-MM-DD") : "",
        endDate: values.endDate ? values.endDate.format("YYYY-MM-DD") : "",
        description: values.description || "",
        selectedItems: selectedItemsPayload
      };

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/quotations/${offer._id}/convert-to-invoice`,
        payload,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        }
      );

      if (res.status === 200 || res.status === 201) {
        Success(t("Invoice created successfully"));
        onClose();
        if (onSuccess) onSuccess();
      } else {
        Error(res.data?.errorMessage || t("Failed to create invoice"));
      }
    } catch (err) {
      if (err?.errorFields) {
        // Form validation error
        return;
      }
      generalErrorAlert(err);
    } finally {
      setLoading(false);
    }
  };

  const currencySymbol =
    offer?.currency === "USD" ? "$" : offer?.currency === "EUR" ? "€" : offer?.currency || "€";

  const columns = [
    {
      title: (
        <Checkbox
          indeterminate={
            selectedItemIds.length > 0 && selectedItemIds.length < availableItems.length
          }
          checked={availableItems.length > 0 && selectedItemIds.length === availableItems.length}
          onChange={handleSelectAll}
          disabled={availableItems.length === 0}
        />
      ),
      key: "select",
      width: 48,
      align: "center",
      render: (_, record) => {
        const remaining = Math.max(0, (record.quantity || 0) - (record.invoicedQuantity || 0));
        const isFullyInvoiced = remaining <= 0;
        return (
          <Checkbox
            checked={selectedItemIds.includes(record._id)}
            disabled={isFullyInvoiced}
            onChange={e => handleCheckboxChange(record._id, e.target.checked)}
          />
        );
      }
    },
    {
      title: t("Product / Service"),
      dataIndex: "description",
      key: "description",
      render: (text, record) => (
        <div>
          <Text strong>{text || t("Item")}</Text>
          {record.duration && (
            <div className="offer-invoice__duration">
              {t("Duration")}: {record.duration}
            </div>
          )}
        </div>
      )
    },
    {
      title: t("Unit"),
      dataIndex: "unit",
      key: "unit",
      width: 90,
      align: "center",
      render: val => <Tag color="default">{val || "-"}</Tag>
    },
    {
      title: t("Unit Price"),
      dataIndex: "price",
      key: "price",
      width: 110,
      align: "right",
      render: val => `${parseFloat(val || 0).toFixed(2)} ${currencySymbol}`
    },
    {
      title: t("Offer Qty"),
      dataIndex: "quantity",
      key: "quantity",
      width: 90,
      align: "center"
    },
    {
      title: t("Already Invoiced"),
      dataIndex: "invoicedQuantity",
      key: "invoicedQuantity",
      width: 120,
      align: "center",
      render: val => (
        <Text style={{ color: val > 0 ? token.colorInfo : token.colorTextSecondary }}>
          {val || 0}
        </Text>
      )
    },
    {
      title: t("Available"),
      key: "remaining",
      width: 100,
      align: "center",
      render: (_, record) => {
        const remaining = Math.max(0, (record.quantity || 0) - (record.invoicedQuantity || 0));
        return <Tag color={remaining > 0 ? "green" : "red"}>{remaining}</Tag>;
      }
    },
    {
      title: t("Invoice Qty"),
      key: "invoiceQty",
      width: 130,
      align: "center",
      render: (_, record) => {
        const remaining = Math.max(0, (record.quantity || 0) - (record.invoicedQuantity || 0));
        const isSelected = selectedItemIds.includes(record._id);
        return (
          <InputNumber
            min={1}
            max={remaining}
            value={isSelected ? quantities[record._id] || 1 : 0}
            disabled={!isSelected || remaining <= 0}
            onChange={val => handleQuantityChange(record._id, val)}
            style={{ width: "100%", maxWidth: 110 }}
          />
        );
      }
    },
    {
      title: t("Line Total"),
      key: "lineTotal",
      width: 110,
      align: "right",
      render: (_, record) => {
        const isSelected = selectedItemIds.includes(record._id);
        const qty = isSelected ? quantities[record._id] || 0 : 0;
        const total = (parseFloat(record.price || 0) * qty).toFixed(2);
        return (
          <Text strong style={{ color: isSelected ? token.colorPrimary : token.colorTextDisabled }}>
            {total} {currencySymbol}
          </Text>
        );
      }
    }
  ];

  // Phones: one stacked card per offer position instead of the 9-column grid.
  const renderMobileLines = () => (
    <div className="offer-invoice-lines">
      <Checkbox
        className="offer-invoice-lines__all"
        indeterminate={selectedItemIds.length > 0 && selectedItemIds.length < availableItems.length}
        checked={availableItems.length > 0 && selectedItemIds.length === availableItems.length}
        onChange={handleSelectAll}
        disabled={availableItems.length === 0}
      >
        {t("Select all")}
      </Checkbox>
      {(offer?.items || []).map(record => {
        const remaining = Math.max(0, (record.quantity || 0) - (record.invoicedQuantity || 0));
        const isSelected = selectedItemIds.includes(record._id);
        const qty = isSelected ? quantities[record._id] || 0 : 0;
        const total = (parseFloat(record.price || 0) * qty).toFixed(2);
        return (
          <div
            key={record._id}
            className={`offer-invoice-line${isSelected ? "" : " offer-invoice-line--off"}`}
          >
            <div className="offer-invoice-line__head">
              <Checkbox
                checked={isSelected}
                disabled={remaining <= 0}
                onChange={e => handleCheckboxChange(record._id, e.target.checked)}
                aria-label={record.description || t("Item")}
              />
              <div className="offer-invoice-line__name">
                <Text strong>{record.description || t("Item")}</Text>
                {record.duration && (
                  <div className="offer-invoice__duration">
                    {t("Duration")}: {record.duration}
                  </div>
                )}
              </div>
              <Tag color={remaining > 0 ? "green" : "red"}>
                {t("Available")}: {remaining}
              </Tag>
            </div>
            <div className="offer-invoice-line__grid">
              <div>
                <span className="offer-invoice-line__label">{t("Unit Price")}</span>
                <span className="offer-invoice-line__value">
                  {parseFloat(record.price || 0).toFixed(2)} {currencySymbol}
                </span>
                <span className="offer-invoice-line__hint">{record.unit || "-"}</span>
              </div>
              <div>
                <span className="offer-invoice-line__label">{t("Offer Qty")}</span>
                <span className="offer-invoice-line__value">{record.quantity}</span>
                <span className="offer-invoice-line__hint">
                  {t("Already Invoiced")}: {record.invoicedQuantity || 0}
                </span>
              </div>
              <div>
                <span className="offer-invoice-line__label">{t("Invoice Qty")}</span>
                <InputNumber
                  min={1}
                  max={remaining}
                  value={isSelected ? quantities[record._id] || 1 : 0}
                  disabled={!isSelected || remaining <= 0}
                  onChange={val => handleQuantityChange(record._id, val)}
                  inputMode="decimal"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <div className="offer-invoice-line__total">
              <Text type="secondary">{t("Line Total")}</Text>
              <Text
                strong
                className="offer-invoice-line__total-value"
                style={{ color: isSelected ? token.colorPrimary : token.colorTextDisabled }}
              >
                {total} {currencySymbol}
              </Text>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <Modal
      className="offer-invoice-modal"
      title={
        <Space wrap>
          <span style={{ color: token.colorPrimary, fontWeight: 600 }}>
            {t("Create Invoice from Offer")}
          </span>
          <Tag color="blue">{offer?.offerNumber || offer?.quotationNumber}</Tag>
          <Tagcomp text={offer?.status} />
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={1050}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose} disabled={loading}>
          {t("Cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          disabled={selectedItemIds.length === 0}
          onClick={handleSubmit}
        >
          {t("Create Invoice")} ({financialSummary.grandTotal.toFixed(2)} {currencySymbol})
        </Button>
      ]}
    >
      <div className="offer-invoice">
        {/* AO-3: invoiced / received / credited / left to invoice, order confirmation and
            Abschlag by progress */}
        {offer?._id ? (
          <Card size="small" style={{ marginBottom: 12 }}>
            <OfferBillingPanel offer={offer} compact />
          </Card>
        ) : null}
        {/* Customer & Offer Details Preview */}
        <Card size="small" className="offer-invoice__meta">
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} bordered={false}>
            <Descriptions.Item label={<Text type="secondary">{t("Client")}</Text>}>
              <Text strong>
                {offer?.client?.firstName} {offer?.client?.lastName}
              </Text>
              {offer?.client?.company && (
                <div className="offer-invoice__meta-sub">{offer.client.company}</div>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={<Text type="secondary">{t("Email / Phone")}</Text>}>
              <div>{offer?.client?.email || "-"}</div>
              <div className="offer-invoice__meta-sub">{offer?.client?.phone || ""}</div>
            </Descriptions.Item>
            <Descriptions.Item label={<Text type="secondary">{t("Currency & Tax")}</Text>}>
              <Tag color="purple">{offer?.currency || "EUR"}</Tag>
              <Tag color="orange">
                {offer?.tax || "19"}% {t("Tax")}
              </Tag>
            </Descriptions.Item>
            {offer?.objectName && (
              <Descriptions.Item label={<Text type="secondary">{t("Object Name")}</Text>}>
                {offer.objectName}
              </Descriptions.Item>
            )}
            {offer?.objectAddress && (
              <Descriptions.Item label={<Text type="secondary">{t("Object Address")}</Text>}>
                {offer.objectAddress}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Invoice Parameters Form */}
        <Form form={form} layout="vertical">
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="title"
                label={t("Invoice Title")}
                rules={[{ required: true, message: t("Title is required") }]}
              >
                <Input placeholder={t("e.g. Invoice #1")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="startDate" label={t("Start Date")}>
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="dueDate"
                label={t("Due Date")}
                rules={[{ required: true, message: t("Due Date is required") }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
          </Row>

          {/* Offer Products Selection */}
          <div className="offer-invoice__items">
            <div className="offer-invoice__items-head">
              <Title level={5} style={{ margin: 0, color: token.colorPrimary }}>
                {t("Select Offer Items to Invoice")}
              </Title>
              <Text type="secondary">
                {selectedItemIds.length} {t("of")} {availableItems.length} {t("items selected")}
              </Text>
            </div>

            {isMobile ? (
              renderMobileLines()
            ) : (
              <ResponsiveTable
                dataSource={offer?.items || []}
                columns={columns}
                rowKey="_id"
                pagination={false}
                size="small"
                bordered
                mobileLayout="table"
              />
            )}
          </div>

          {/* Live Summary Section */}
          <Row justify="end" className="offer-invoice__summary">
            <Col xs={24} sm={12} md={8}>
              <Card size="small" className="offer-invoice__summary-card">
                <div className="offer-invoice__summary-row">
                  <Text>{t("Subtotal")}:</Text>
                  <Text strong>
                    {financialSummary.subTotal.toFixed(2)} {currencySymbol}
                  </Text>
                </div>
                <div className="offer-invoice__summary-row">
                  <Text>
                    {t("Tax")} ({financialSummary.taxRate}%):
                  </Text>
                  <Text>
                    {financialSummary.taxAmount.toFixed(2)} {currencySymbol}
                  </Text>
                </div>
                <Divider style={{ margin: "8px 0" }} />
                <div className="offer-invoice__summary-row">
                  <Title level={5} style={{ margin: 0 }}>
                    {t("Total")}:
                  </Title>
                  <Title level={4} style={{ margin: 0, color: token.colorPrimary }}>
                    {financialSummary.grandTotal.toFixed(2)} {currencySymbol}
                  </Title>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Notes / Description */}
          <Form.Item name="description" label={t("Notes / Terms")}>
            <TextArea rows={2} placeholder={t("Additional invoice notes...")} />
          </Form.Item>
        </Form>

        {/* Existing Linked Invoices Section */}
        {existingInvoices.length > 0 && (
          <div className="offer-invoice__history">
            <Divider orientation="left">
              <Text type="secondary">
                {t("Invoices Previously Created from this Offer")} ({existingInvoices.length})
              </Text>
            </Divider>
            <ResponsiveTable
              dataSource={existingInvoices}
              rowKey="_id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: "#",
                  dataIndex: "title",
                  mobile: "title",
                  render: (text, rec) => (
                    <div>
                      <Text strong>{text || rec._id}</Text>
                    </div>
                  )
                },
                {
                  title: t("Total Amount"),
                  dataIndex: "totalAmount",
                  render: (val, rec) => (
                    <Text strong>
                      {parseFloat(val || 0).toFixed(2)} {rec.currency || currencySymbol}
                    </Text>
                  )
                },
                {
                  title: t("Date"),
                  dataIndex: "createdAt",
                  render: val => dayjs(val).format("DD.MM.YYYY")
                },
                {
                  title: t("Status"),
                  dataIndex: "status",
                  mobile: "subtitle",
                  render: text => <Tagcomp text={text} />
                }
              ]}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CreateInvoiceModal;
